import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import type {
	AdminSettingCompanySetting,
	CompanySetting
} from "~/models/CompanySetting";
import { updateCompanyInformation } from "~/utils/api/admin/CompanySetting";
import { isSuccess } from "~/utils/api/api_handler";
import {
	showNewFeatureExcludePrd,
	showNewFeatureForLocal
} from "~/utils/FeatureFlagUtil";
import { generatePath } from "~/utils/generatePath";

type Props = {
	companySetting: CompanySetting;
};
export function CompanySettingForm({ companySetting }: Props) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleOpenModal = () => {
		setIsModalOpen(true);
	};
	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const { control, register, handleSubmit } =
		useForm<AdminSettingCompanySetting>({ defaultValues: companySetting });

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/itandi_account_sessions/current_account"),
		async (_key, { arg }: { arg: AdminSettingCompanySetting }) =>
			updateCompanyInformation(arg)
	);

	const handleAccept = useCallback(
		async (submitValue: AdminSettingCompanySetting) => {
			const response = await trigger(submitValue);
			if (isSuccess(response)) {
				toast.success("保存しました。");
				handleCloseModal();
			} else {
				toast.error("エラーが発生しました。");
			}
		},
		[trigger]
	);

	return (
		<Box width={360}>
			<FormGroup variant="vertical">
				<FormControl label="請求管理ロボ用請求先コード">
					<Input {...register("robotPaymentCode")} />
				</FormControl>
				<FormControl label="施工会社招待機能">
					<Controller
						control={control}
						name="fromMarket"
						render={({ field }) => (
							<InputCheckBox
								//  UI上の「有効化」操作と、DBフラグ（from_market）の意味が逆転する形になるので反転する
								checked={!field.value}
								label="取引先の施工会社を追加・招待する"
								onChange={(e) => {
									field.onChange(!e.currentTarget.checked);
								}}
							/>
						)}
					/>
					<FormControlText
						help={[
							"SaaS単体、またはSaaS+マケプレ導入の場合はチェックを入れてください",
							"チェックを入れると、管理会社一覧画面の反社チェックID入力ボタンは非表示になります"
						]}
					/>
				</FormControl>
				<FormControl label="精算書発行機能">
					<Controller
						control={control}
						name="settlementEnabled"
						render={({ field }) => (
							<InputCheckBox
								checked={field.value}
								label="請求書画面で精算書を発行する"
								onChange={(e) => {
									field.onChange(e.currentTarget.checked);
								}}
							/>
						)}
					/>
					<FormControlText help="チェックを外すと、精算項目の追加と精算書ダウンロードが利用できなくなります" />
				</FormControl>
				<FormControl label="物件情報の登録制御">
					<Controller
						control={control}
						name="onlyAmaterasProperties"
						render={({ field }) => (
							<InputCheckBox
								checked={field.value}
								label="物件システムに登録されている物件のみ表示する"
								onChange={(e) => {
									field.onChange(e.currentTarget.checked);
								}}
							/>
						)}
					/>
				</FormControl>
				<FormControl label="イタンジアカウントの担当者の利用">
					<Controller
						control={control}
						name="useIaStaffLogin"
						render={({ field }) => (
							<InputCheckBox
								checked={field.value}
								label="イタンジアカウントの担当者を利用する"
								onChange={(e) => {
									field.onChange(e.currentTarget.checked);
								}}
							/>
						)}
					/>
					<FormControlText help="有効にすると、修繕システム上で担当者を追加・編集ができなくなります" />
				</FormControl>
				{showNewFeatureForLocal() ? (
					<FormControl label="立会日の入居者システムへの自動反映">
						<Controller
							control={control}
							name="syncInspectionDateToKt"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value}
									label="確定した立会日を入居者システムに自動反映する"
									onChange={(e) => {
										field.onChange(e.currentTarget.checked);
									}}
								/>
							)}
						/>
						<FormControlText help="有効にすると、立会日を確定・更新した際に入居者システムへ自動で反映されます" />
					</FormControl>
				) : null}
				{showNewFeatureExcludePrd() ? (
					<FormControl label="案件詳細画面V2の利用">
						<Alertv2 mb={designTokens["spacing/m"]} variant="warning">
							<Paragraph>
								顧客画面に影響するため、PdM以外は操作禁止です。
							</Paragraph>
						</Alertv2>
						<Controller
							control={control}
							name="useV2DealDetail"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value}
									label="案件詳細画面V2を利用する"
									onChange={(e) => {
										field.onChange(e.currentTarget.checked);
									}}
								/>
							)}
						/>
						<FormControlText help="有効にすると、案件詳細画面V2を利用できます" />
					</FormControl>
				) : null}
			</FormGroup>
			<Buttonv2
				marginTop={designTokens["spacing/m"]}
				onClick={handleOpenModal}
				variant="primary"
			>
				保存
			</Buttonv2>
			{isModalOpen ? (
				<Modalv2 isOpen onClose={handleCloseModal} size="s">
					<ModalHeader title="会社設定の保存" />
					<ModalBody>会社設定を保存します。</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isMutating}
								onClick={handleCloseModal}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								loading={isMutating}
								onClick={handleSubmit(handleAccept)}
								variant="primary"
							>
								保存
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</Box>
	);
}
